/**
 * SearchFilters Component
 * Advanced filtering options for content search
 * Features:
 * - Filter by content type
 * - Filter by folder
 * - Filter by tags
 * - Sort options
 * - Active filters display with clear
 */

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  X,
  FileText,
  Hash,
  Image,
  Table,
  Mail,
  Presentation,
  Folder,
  Tag,
  SortAsc,
  SortDesc,
  ChevronDown,
} from 'lucide-react'
import type { ContentItemType, ContentTag } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface SimpleFolderOption {
  id: string
  name: string
  path?: string
}

export interface SearchFiltersProps {
  selectedTypes: ContentItemType[]
  onTypesChange: (types: ContentItemType[]) => void
  selectedFolderId?: string
  onFolderChange: (folderId: string | undefined) => void
  selectedTags: string[]
  onTagsChange: (tags: string[]) => void
  sortBy: string
  sortOrder: 'asc' | 'desc'
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void
  folders?: SimpleFolderOption[]
  tags?: ContentTag[]
  className?: string
}

interface TypeOption {
  value: ContentItemType
  label: string
  icon: React.ElementType
  color: string
}

interface SortOption {
  value: string
  label: string
}

// ============================================
// Constants
// ============================================

const TYPE_OPTIONS: TypeOption[] = [
  { value: 'Text', label: 'Text', icon: FileText, color: 'text-blue-500' },
  { value: 'DataPoint', label: 'Data Point', icon: Hash, color: 'text-green-500' },
  { value: 'Image', label: 'Image', icon: Image, color: 'text-purple-500' },
  { value: 'Table', label: 'Table', icon: Table, color: 'text-amber-500' },
  { value: 'EmailSnippet', label: 'Email', icon: Mail, color: 'text-rose-500' },
  { value: 'SlideElement', label: 'Slide', icon: Presentation, color: 'text-orange-500' },
]

const SORT_OPTIONS: SortOption[] = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'name', label: 'Name' },
  { value: 'updatedAt', label: 'Date Modified' },
  { value: 'createdAt', label: 'Date Created' },
  { value: 'usageCount', label: 'Usage' },
]

// ============================================
// SearchFilters Component
// ============================================

export function SearchFilters({
  selectedTypes,
  onTypesChange,
  selectedFolderId,
  onFolderChange,
  selectedTags,
  onTagsChange,
  sortBy,
  sortOrder,
  onSortChange,
  folders = [],
  tags = [],
  className,
}: SearchFiltersProps) {
  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (selectedTypes.length > 0) count += selectedTypes.length
    if (selectedFolderId) count += 1
    if (selectedTags.length > 0) count += selectedTags.length
    return count
  }, [selectedTypes, selectedFolderId, selectedTags])

  // Toggle content type
  const toggleType = (type: ContentItemType) => {
    if (selectedTypes.includes(type)) {
      onTypesChange(selectedTypes.filter((t) => t !== type))
    } else {
      onTypesChange([...selectedTypes, type])
    }
  }

  // Toggle tag
  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter((t) => t !== tag))
    } else {
      onTagsChange([...selectedTags, tag])
    }
  }

  // Clear all filters
  const clearAllFilters = () => {
    onTypesChange([])
    onFolderChange(undefined)
    onTagsChange([])
  }

  // Toggle sort order
  const toggleSortOrder = () => {
    onSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')
  }

  // Get current folder name
  const currentFolderName = useMemo(() => {
    if (!selectedFolderId || !folders) return null
    const folder = folders.find((f) => f.id === selectedFolderId)
    return folder?.name ?? null
  }, [selectedFolderId, folders])

  return (
    <div className={cn('space-y-3', className)}>
      {/* Filter Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Content Type Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                'h-9 rounded-lg gap-1.5',
                selectedTypes.length > 0 && 'border-blue-500 text-blue-600'
              )}
            >
              <FileText className="h-4 w-4" />
              Type
              {selectedTypes.length > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1 h-5 px-1.5 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                >
                  {selectedTypes.length}
                </Badge>
              )}
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 px-2 py-1">
              Content Types
            </p>
            {TYPE_OPTIONS.map((type) => {
              const Icon = type.icon
              const isSelected = selectedTypes.includes(type.value)

              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => toggleType(type.value)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg',
                    'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                    isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                  )}
                >
                  <Checkbox checked={isSelected} className="h-4 w-4" />
                  <Icon className={cn('h-4 w-4', type.color)} />
                  <span className="text-sm">{type.label}</span>
                </button>
              )
            })}
          </PopoverContent>
        </Popover>

        {/* Folder Filter */}
        {folders.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  'h-9 rounded-lg gap-1.5',
                  selectedFolderId && 'border-blue-500 text-blue-600'
                )}
              >
                <Folder className="h-4 w-4" />
                {currentFolderName || 'Folder'}
                <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 px-2 py-1">
                Folder
              </p>
              <button
                type="button"
                onClick={() => onFolderChange(undefined)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left',
                  'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                  !selectedFolderId && 'bg-blue-50 dark:bg-blue-900/20'
                )}
              >
                <span className="text-sm">All Folders</span>
              </button>
              <Separator className="my-1" />
              <ScrollArea className="max-h-[200px]">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => onFolderChange(folder.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left',
                      'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                      selectedFolderId === folder.id && 'bg-blue-50 dark:bg-blue-900/20'
                    )}
                  >
                    <Folder className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm truncate">{folder.name}</span>
                  </button>
                ))}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}

        {/* Tags Filter */}
        {tags.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  'h-9 rounded-lg gap-1.5',
                  selectedTags.length > 0 && 'border-blue-500 text-blue-600'
                )}
              >
                <Tag className="h-4 w-4" />
                Tags
                {selectedTags.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 px-1.5 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                  >
                    {selectedTags.length}
                  </Badge>
                )}
                <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 px-2 py-1">
                Filter by Tags
              </p>
              <ScrollArea className="max-h-[250px]">
                {tags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.name)

                  return (
                    <button
                      key={tag.name}
                      type="button"
                      onClick={() => toggleTag(tag.name)}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg',
                        'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                        isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                      )}
                    >
                      <Checkbox checked={isSelected} className="h-4 w-4" />
                      <span className="text-sm flex-1 text-left">#{tag.name}</span>
                      <span className="text-xs text-neutral-400">{tag.usageCount}</span>
                    </button>
                  )
                })}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}

        {/* Divider */}
        <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-700" />

        {/* Sort */}
        <div className="flex items-center gap-1">
          <Select value={sortBy} onValueChange={(value) => onSortChange(value, sortOrder)}>
            <SelectTrigger className="h-9 w-[140px] rounded-lg">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleSortOrder}
            className="h-9 w-9 rounded-lg"
          >
            {sortOrder === 'asc' ? (
              <SortAsc className="h-4 w-4" />
            ) : (
              <SortDesc className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Clear All */}
        {activeFilterCount > 0 && (
          <>
            <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-700" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-9 text-neutral-500 hover:text-red-600"
            >
              <X className="h-4 w-4 mr-1" />
              Clear filters
            </Button>
          </>
        )}
      </div>

      {/* Active Filters */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            Active filters:
          </span>

          {/* Type badges */}
          {selectedTypes.map((type) => {
            const option = TYPE_OPTIONS.find((t) => t.value === type)
            if (!option) return null
            const Icon = option.icon

            return (
              <Badge
                key={type}
                variant="secondary"
                className="gap-1 pr-1"
              >
                <Icon className={cn('h-3 w-3', option.color)} />
                {option.label}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleType(type)}
                  className="h-4 w-4 ml-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full"
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            )
          })}

          {/* Folder badge */}
          {selectedFolderId && currentFolderName && (
            <Badge variant="secondary" className="gap-1 pr-1">
              <Folder className="h-3 w-3" />
              {currentFolderName}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onFolderChange(undefined)}
                className="h-4 w-4 ml-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {/* Tag badges */}
          {selectedTags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1 pr-1">
              #{tag}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => toggleTag(tag)}
                className="h-4 w-4 ml-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

export default SearchFilters
